import type { ComponentType } from 'react';
import type { PopoverProps } from 'antd';

import type { WorkspaceResource } from '@/hooks/useWorkspaceResourceAccess';
import {
    dashboardService,
    folderService,
    investmentService,
    lifecycleService,
    listService,
    memoService,
    pipelineService,
} from '@/services/database';
import type { WorkspaceResourcePreviewContentProps } from './types';

export interface WorkspaceResourcePreviewRegistryEntry {
    defaultPlacement?: PopoverProps['placement'];
    loadDetail: (id: string) => Promise<unknown>;
    loadPreviewComponent?: () => Promise<{ default: ComponentType<WorkspaceResourcePreviewContentProps> }>;
    restore?: (id: string) => Promise<unknown>;
}

/**
 * Single registry for workspace-resource hover previews. Generic resources use
 * the shared card renderer; richer resources may register a specialized body
 * without replacing the trigger, popover shell, permissions, or placement path.
 */
export const WORKSPACE_RESOURCE_PREVIEW_REGISTRY: Record<WorkspaceResource, WorkspaceResourcePreviewRegistryEntry> = {
    dashboards: {
        loadDetail: (id) => dashboardService.getById(id),
        restore: (id) => dashboardService.restore(id),
    },
    folders: {
        loadDetail: (id) => folderService.getById(id),
        restore: (id) => folderService.restore(id),
    },
    investments: {
        loadDetail: (id) => investmentService.getById(id),
        restore: (id) => investmentService.restore(id),
    },
    lifecycles: {
        defaultPlacement: 'bottomLeft',
        loadDetail: (id) => lifecycleService.getById(id),
        loadPreviewComponent: () => import('@/shared/components/lifecycles/LifecycleWorkspaceResourcePreview'),
    },
    lists: {
        loadDetail: (id) => listService.getById(id),
        restore: (id) => listService.restore(id),
    },
    memos: {
        loadDetail: (id) => memoService.getById(id),
        restore: (id) => memoService.restore(id),
    },
    pipelines: {
        loadDetail: (id) => pipelineService.getById(id),
        restore: (id) => pipelineService.restore(id),
    },
};

export const workspaceResourcePreviewPlacement = (
    resourceType: string,
    requestedPlacement: PopoverProps['placement'] | undefined,
    fallback: PopoverProps['placement'],
) =>
    requestedPlacement ??
    WORKSPACE_RESOURCE_PREVIEW_REGISTRY[resourceType as WorkspaceResource]?.defaultPlacement ??
    fallback;
